import { useState, useEffect, useRef, ReactNode } from "react";

// ════════════════════════════════════════════════════════════
//  TYPES
// ════════════════════════════════════════════════════════════

interface FormState {
  symbol:   string;
  qty:      string;
  avgPrice: string;
  ltp:      string;
  target:   string;
}

interface ScenarioRow {
  qty:      number;
  cost:     number;
  newAvg:   number;
  diff:     number;
  isTarget: boolean;
}

interface CalculatorResult {
  sharesToBuy:  number;
  newAvg:       number;
  addCost:      number;
  totalCost:    number;
  totalQty:     number;
  pnl:          number;
  pnlPct:       number;
  progress:     number;
  scenarios:    ScenarioRow[];
  verdict:      "buy" | "hold" | "sell";
  verdictText:  string;
  analysisText: string;
}

// ════════════════════════════════════════════════════════════
//  UTILITIES
// ════════════════════════════════════════════════════════════

const inr = (n: number, dec = 2): string =>
  "₹" + parseFloat(String(n)).toLocaleString("en-IN", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

const inrPdf = (n: number, dec = 2): string =>
  "Rs." + parseFloat(String(n)).toLocaleString("en-IN", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

// ════════════════════════════════════════════════════════════
//  HOOK — useCalculator
// ════════════════════════════════════════════════════════════

function useCalculator() {
  const [form, setForm] = useState<FormState>({
    symbol: "IOC", qty: "52", avgPrice: "158.61", ltp: "144.70", target: "150",
  });
  const [result, setResult]       = useState<CalculatorResult | null>(null);
  const [error, setError]         = useState<string>("");
  const [exporting, setExporting] = useState(false);

  const setField = (key: keyof FormState) => (v: string) =>
    setForm(f => ({ ...f, [key]: v }));

  const calculate = () => {
    const qty    = parseFloat(form.qty);
    const avg    = parseFloat(form.avgPrice);
    const ltp    = parseFloat(form.ltp);
    const target = parseFloat(form.target);
    const sym    = form.symbol.trim().toUpperCase() || "STOCK";

    if ([qty, avg, ltp, target].some(isNaN) || qty <= 0 || ltp <= 0) {
      setError("Please fill in all fields correctly."); return;
    }
    if (target >= avg) {
      setError("Target avg must be lower than your current avg to buy-down."); return;
    }
    if (ltp >= target) {
      setError("Current LTP is already above your target avg — no need to buy more!"); return;
    }
    setError("");

    const invested   = qty * avg;
    const xExact     = (invested - target * qty) / (target - ltp);
    const xQty       = Math.ceil(xExact);
    const addCost    = xQty * ltp;
    const totalQty   = qty + xQty;
    const totalCost  = invested + addCost;
    const newAvg     = totalCost / totalQty;
    const pnl        = (ltp - newAvg) * totalQty;
    const pnlPct     = ((ltp - newAvg) / newAvg) * 100;
    const progress   = Math.min(100, Math.max(0, (ltp / newAvg) * 100));

    const steps     = [-20, -10, 0, 10, 20, 30].map(d => Math.max(1, xQty + d));
    const scenarios = [...new Set(steps)].map(q => {
      const c = invested + q * ltp;
      const a = c / (qty + q);
      return { qty: q, cost: q * ltp, newAvg: a, diff: a - target, isTarget: q === xQty };
    });

    const beGap        = ((newAvg - ltp) / ltp) * 100;
    const ltpDropPct   = ((avg - ltp) / avg) * 100;
    const riskMultiple = addCost / invested;

    let verdict: "buy" | "hold" | "sell";
    let verdictText: string;
    let analysisText: string;

    if (riskMultiple > 2) {
      verdict      = "sell";
      verdictText  = "HIGH RISK — Consider Exiting";
      analysisText = `You are adding ${inrPdf(addCost, 0)} to average down — that's ${riskMultiple.toFixed(1)}× your original investment. For ${sym}, this is a high-risk bet. The stock needs to recover ${beGap.toFixed(1)}% just to break even. If fundamentals are weak or trend is bearish, cutting losses may be wiser.`;
    } else if (beGap > 15) {
      verdict      = "hold";
      verdictText  = "CAUTION — Hold & Watch";
      analysisText = `Stock needs ${beGap.toFixed(1)}% upside to break even after averaging. ${sym} has dropped ${ltpDropPct.toFixed(1)}% from your buy price. Buying ${xQty} shares makes sense only if you have high conviction in a recovery within 6–12 months. Monitor support levels carefully.`;
    } else {
      verdict      = "buy";
      verdictText  = "REASONABLE — Averaging is Smart";
      analysisText = `Buying ${xQty} shares of ${sym} at ${inrPdf(ltp)} brings your avg to ${inrPdf(newAvg)}, requiring only ${beGap.toFixed(1)}% recovery to break even. Additional cost of ${inrPdf(addCost, 0)} is ${(riskMultiple * 100).toFixed(0)}% of original investment — a measured, low-risk strategy.`;
    }

    setResult({ sharesToBuy: xQty, newAvg, addCost, totalCost, totalQty, pnl, pnlPct, progress, scenarios, verdict, verdictText, analysisText });
  };

  const exportPdf = async () => {
    if (!result) return;
    setExporting(true);
    try {
      if (!(window as any).jspdf) {
        await new Promise<void>((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
          s.onload = () => res();
          s.onerror = () => rej(new Error("Failed to load jsPDF"));
          document.head.appendChild(s);
        });
      }
      await buildPdf(form, result);
    } catch (e) {
      alert("PDF export failed. Please try again.");
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  return { form, setField, result, error, exporting, calculate, exportPdf };
}

// ════════════════════════════════════════════════════════════
//  PDF BUILDER  (improved position card spacing)
// ════════════════════════════════════════════════════════════

async function buildPdf(form: FormState, r: CalculatorResult) {
  const { jsPDF } = (window as any).jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  const W        = 210;
  const H        = 297;           // A4 height
  const M        = 14;
  const CW       = W - M * 2;
  const FOOTER_H = 18;            // reserved space at bottom for footer
  const SAFE_BOT = H - FOOTER_H; // content must stay above this line
  let   y        = 0;

  const RED   : [number,number,number] = [204, 20,  20 ];
  const ROYAL : [number,number,number] = [65,  105, 225];
  const DARK  : [number,number,number] = [26,  26,  46 ];
  const MUTED : [number,number,number] = [107, 114, 128];
  const GREEN : [number,number,number] = [22,  163, 74 ];
  const LBRD  : [number,number,number] = [221, 225, 240];
  const SURF  : [number,number,number] = [248, 249, 252];

  // ── Draw the footer on whatever the current last page is ──
  const drawFooter = () => {
    const fy = H - FOOTER_H + 4;
    doc.setFillColor(...RED);   doc.rect(0,       H - FOOTER_H, W * 0.5, 0.8, "F");
    doc.setFillColor(...ROYAL); doc.rect(W * 0.5, H - FOOTER_H, W * 0.5, 0.8, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...MUTED);
    doc.text(
      "Not SEBI-registered advice. For educational purposes only. Always do your own research.",
      W / 2, fy + 2, { align: "center" }
    );
    doc.text(
      `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
      W / 2, fy + 6, { align: "center" }
    );
  };

  // ── Auto page-break: call before drawing any block that needs `neededMm` space ──
  const ensureSpace = (neededMm: number) => {
    if (y + neededMm > SAFE_BOT) {
      drawFooter();                 // close out current page
      doc.addPage();
      y = 14;                       // top margin on new page
    }
  };

  // ── Header (page 1 only) ──
  doc.setFillColor(...RED);
  doc.rect(0, 0, W, 36, "F");
  doc.setFillColor(255, 255, 255);
  doc.setGState(doc.GState({ opacity: 0.06 }));
  doc.circle(W - 10, 5, 20, "F");
  doc.circle(10, 30, 14, "F");
  doc.setGState(doc.GState({ opacity: 1 }));
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");   doc.setFontSize(20);
  doc.text("Stock Averaging Calculator", W / 2, 16, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text("Plan your buy-down strategy — For educational purposes only", W / 2, 24, { align: "center" });
  doc.setFillColor(255, 255, 255);
  doc.setGState(doc.GState({ opacity: 0.15 }));
  doc.roundedRect(W / 2 - 24, 27, 48, 6, 3, 3, "F");
  doc.setGState(doc.GState({ opacity: 1 }));
  doc.setFontSize(7);
  doc.text("STOCK TOOL", W / 2, 31, { align: "center" });
  doc.setFillColor(...RED);   doc.rect(0,       36, W * 0.5, 1.5, "F");
  doc.setFillColor(...ROYAL); doc.rect(W * 0.5, 36, W * 0.5, 1.5, "F");
  y = 44;

  // ── Helpers ──
  const sectionTitle = (t: string) => {
    ensureSpace(14);
    doc.setFillColor(...RED);
    doc.rect(M, y, 2.5, 5, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text(t.toUpperCase(), M + 5, y + 4);
    y += 9;
  };

  const divider = () => {
    ensureSpace(8);
    doc.setDrawColor(...LBRD); doc.setLineWidth(0.3);
    doc.line(M, y, W - M, y); y += 5;
  };

  // ── YOUR POSITION card ──
  sectionTitle("Your Position");

  const posItems = [
    { label: "Symbol",              value: form.symbol.toUpperCase() },
    { label: "Qty Held",            value: form.qty + " shares" },
    { label: "Avg Buy Price",       value: inrPdf(parseFloat(form.avgPrice)) },
    { label: "Current LTP",         value: inrPdf(parseFloat(form.ltp)) },
    { label: "Target Avg Price",    value: inrPdf(parseFloat(form.target)) },
    { label: "Original Investment", value: inrPdf(parseFloat(form.qty) * parseFloat(form.avgPrice), 0) },
  ];

  const posRows   = 3;
  const posRowH   = 16;
  const posPadTop = 8;
  const posPadBot = 8;
  const cardH     = posPadTop + posRows * posRowH + posPadBot;
  const colW      = CW / 2;

  ensureSpace(cardH + 6);

  doc.setFillColor(...SURF);
  doc.roundedRect(M, y, CW, cardH, 3, 3, "F");
  doc.setDrawColor(...LBRD); doc.setLineWidth(0.4);
  doc.roundedRect(M, y, CW, cardH, 3, 3, "S");
  doc.setDrawColor(...LBRD); doc.setLineWidth(0.3);
  doc.line(M + colW, y + 6, M + colW, y + cardH - 6);

  posItems.forEach(({ label, value }, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const ix = M + col * colW + 10;
    const iy = y + posPadTop + row * posRowH;
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text(label, ix, iy + 4);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...DARK);
    doc.text(value, ix, iy + 11);
  });

  for (let row = 1; row < posRows; row++) {
    const ry = y + posPadTop + row * posRowH - 2;
    doc.setDrawColor(235, 237, 245); doc.setLineWidth(0.2);
    doc.line(M + 4, ry, M + CW - 4, ry);
  }
  y += cardH + 6;

  // ── RESULT SUMMARY ──
  sectionTitle("Result Summary");
  const stats: { label: string; value: string; color: [number,number,number] }[] = [
    { label: "Shares to Buy",        value: r.sharesToBuy + " shares",  color: RED  },
    { label: "New Avg Price",         value: inrPdf(r.newAvg),           color: DARK },
    { label: "Additional Investment", value: inrPdf(r.addCost, 0),       color: DARK },
    { label: "Total Investment",      value: inrPdf(r.totalCost, 0),     color: DARK },
    { label: "Total Qty After",       value: r.totalQty + " shares",     color: DARK },
    { label: "Unrealised P&L",
      value: `${r.pnl >= 0 ? "+" : ""}${inrPdf(r.pnl, 0)} (${r.pnlPct.toFixed(2)}%)`,
      color: r.pnl >= 0 ? GREEN : [220, 38, 38] },
  ];
  const sw = (CW - 5) / 3, sh = 20;
  ensureSpace(2 * (sh + 3) + 5);
  for (let row = 0; row < 2; row++) {
    for (let col = 0; col < 3; col++) {
      const idx = row * 3 + col;
      if (idx >= stats.length) break;
      const sx = M + col * (sw + 2.5), sy = y + row * (sh + 3);
      doc.setFillColor(...SURF); doc.setDrawColor(...LBRD); doc.setLineWidth(0.3);
      doc.roundedRect(sx, sy, sw, sh, 2, 2, "FD");
      doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...MUTED);
      doc.text(stats[idx].label.toUpperCase(), sx + 4, sy + 6);
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...stats[idx].color);
      doc.text(stats[idx].value, sx + 4, sy + 15);
    }
  }
  y += 2 * (sh + 3) + 5;

  // ── Breakeven bar ──
  ensureSpace(22);
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text("BREAKEVEN PROGRESS", M, y + 3); y += 7;
  const barColor: [number,number,number] = r.progress >= 95 ? GREEN : r.progress >= 70 ? [245,158,11] : [220,38,38];
  doc.setFillColor(...LBRD); doc.roundedRect(M, y, CW, 4, 2, 2, "F");
  if (r.progress > 0) { doc.setFillColor(...barColor); doc.roundedRect(M, y, CW * (r.progress / 100), 4, 2, 2, "F"); }
  y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...MUTED);
  doc.text(`LTP ${inrPdf(parseFloat(form.ltp))}`, M, y + 4);
  doc.text(`Target ${inrPdf(r.newAvg)}`, W - M, y + 4, { align: "right" });
  y += 10;
  divider();

  // ── Buy Scenarios ──
  sectionTitle("Buy Scenarios");
  const tHeaders = ["Qty to Buy", "Cost (Rs.)", "New Avg (Rs.)", "vs Target"];
  const tCols    = [30, 45, 50, 40];
  const tH = 7;

  // Table header — check space for header row + at least 2 data rows
  ensureSpace(tH * 3);
  doc.setFillColor(...ROYAL); doc.rect(M, y, CW, tH, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(255, 255, 255);
  let tx = M;
  tHeaders.forEach((h, i) => { doc.text(h, tx + 3, y + 4.8); tx += tCols[i]; });
  y += tH;

  r.scenarios.forEach((row, idx) => {
    // Break page before each row if needed
    ensureSpace(tH + 2);
    const bg: [number,number,number] = row.isTarget ? [235,240,255] : idx % 2 === 0 ? [255,255,255] : [248,249,252];
    doc.setFillColor(...bg); doc.rect(M, y, CW, tH, "F");
    doc.setDrawColor(...LBRD); doc.setLineWidth(0.2); doc.line(M, y + tH, M + CW, y + tH);
    const tc = row.isTarget ? ROYAL : DARK;
    doc.setTextColor(...tc); doc.setFont("helvetica", row.isTarget ? "bold" : "normal"); doc.setFontSize(8.5);
    tx = M;
    [row.qty + (row.isTarget ? " *" : ""), inrPdf(row.cost, 0), inrPdf(row.newAvg)].forEach((cell, i) => {
      doc.text(cell, tx + 3, y + 4.8); tx += tCols[i];
    });
    const good = row.diff <= 0.5;
    const bc: [number,number,number] = good ? GREEN : [220,38,38];
    const bb: [number,number,number] = good ? [220,252,231] : [254,226,226];
    const bt = (row.diff >= 0 ? "+" : "") + row.diff.toFixed(2);
    const bw = doc.getStringUnitWidth(bt) * 8.5 / doc.internal.scaleFactor + 4;
    doc.setFillColor(...bb); doc.roundedRect(tx + 1, y + 1.5, bw, 4.5, 2, 2, "F");
    doc.setTextColor(...bc); doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
    doc.text(bt, tx + 3, y + 5);
    y += tH;
  });
  y += 7;

  // ── Strategy Analysis ──
  // Pre-calculate how much space the whole analysis block needs before drawing
  doc.setFontSize(8.5);
  const analysisLines = doc.splitTextToSize(r.analysisText, CW);
  const analysisBlockH = 9 + 8 + 13 + analysisLines.length * 5 + 6 + 8; // sectionTitle+verdict+text+divider
  ensureSpace(analysisBlockH);

  sectionTitle("Strategy Analysis");
  const vc: [number,number,number] = r.verdict === "buy" ? GREEN : r.verdict === "sell" ? [220,38,38] : [217,119,6];
  doc.setFillColor(...SURF); doc.setDrawColor(...LBRD); doc.setLineWidth(0.3);
  doc.roundedRect(M, y, CW, 8, 2, 2, "FD");
  doc.setFillColor(...vc); doc.rect(M, y, 3, 8, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...vc);
  doc.text(r.verdictText, M + 7, y + 5.5);
  y += 13;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
  doc.text(analysisLines, M, y);
  y += analysisLines.length * 5 + 6;
  divider();

  // ── Footer on the last page ──
  drawFooter();

  doc.save(`${form.symbol.toUpperCase() || "STOCK"}_averaging_strategy.pdf`);
}

// ════════════════════════════════════════════════════════════
//  SHARED UI PRIMITIVES
// ════════════════════════════════════════════════════════════

const Highlight = ({ children }: { children: ReactNode }) => (
  <span style={{ color: "#ffffff", background: "rgba(255,255,255,0.15)", padding: "0 10px", borderRadius: 6 }}>
    {children}
  </span>
);

const PageHeader = ({ title, subtitle }: { title: ReactNode; subtitle?: string }) => (
  <header style={{
    background: "linear-gradient(135deg, #cc1414 0%, #a50f0f 60%, #8b0d0d 100%)",
    padding: "36px 24px 40px", textAlign: "center",
    position: "relative", overflow: "hidden",
    boxShadow: "0 4px 24px rgba(160,15,15,0.25)",
  }}>
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: `
        repeating-linear-gradient(0deg, transparent, transparent 29px, rgba(255,255,255,0.04) 29px, rgba(255,255,255,0.04) 30px),
        repeating-linear-gradient(90deg, transparent, transparent 29px, rgba(255,255,255,0.04) 29px, rgba(255,255,255,0.04) 30px)
      `,
    }} />
    <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,0.06)", pointerEvents:"none" }} />
    <div style={{ position:"absolute", bottom:-20, left:-20, width:80,  height:80,  borderRadius:"50%", background:"rgba(255,255,255,0.05)", pointerEvents:"none" }} />
    <div style={{
      fontFamily:"var(--mono)", fontSize:11, letterSpacing:"0.2em",
      color:"rgba(255,255,255,0.75)", textTransform:"uppercase",
      background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.25)",
      padding:"4px 14px", borderRadius:100, display:"inline-block",
      marginBottom:16, position:"relative",
    }}>
      📈 Stock Tool
    </div>
    <h1 style={{ fontSize:"clamp(26px,6vw,44px)", fontWeight:800, letterSpacing:"-0.03em", lineHeight:1.1, color:"#ffffff", position:"relative" }}>
      {title}
    </h1>
    {subtitle && (
      <p style={{ color:"rgba(255,255,255,0.65)", fontSize:13, marginTop:10, fontFamily:"var(--mono)", position:"relative" }}>
        {subtitle}
      </p>
    )}
  </header>
);

const AccentStrip = () => (
  <div style={{ height:4, background:"linear-gradient(90deg,#cc1414 0%,#4169e1 100%)" }} />
);

const Card = ({ children, className, style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) => (
  <div className={className} style={{
    background:"var(--card)", border:"1px solid var(--border)",
    borderRadius:16, padding:24,
    boxShadow:"0 2px 12px rgba(65,105,225,0.07)", ...style,
  }}>
    {children}
  </div>
);

const CardTitle = ({ children }: { children: ReactNode }) => (
  <div style={{
    fontSize:11, fontFamily:"var(--mono)", letterSpacing:"0.14em",
    textTransform:"uppercase", color:"var(--muted)",
    marginBottom:20, display:"flex", alignItems:"center", gap:8,
  }}>
    <span style={{ width:3, height:14, background:"var(--red)", borderRadius:2, display:"inline-block", flexShrink:0 }} />
    {children}
  </div>
);

// ── Field: fully responsive, never wraps ──
const Field = ({
  label, id, type = "text", value, onChange, placeholder, step, fullWidth = false,
}: {
  label: string; id: string; type?: string; value: string;
  onChange: (v: string) => void; placeholder?: string; step?: string; fullWidth?: boolean;
}) => (
  <div style={{ display:"flex", flexDirection:"column", gap:6, gridColumn: fullWidth ? "1 / -1" : undefined }}>
    <label htmlFor={id} style={{
      fontSize:11, fontFamily:"var(--mono)", color:"var(--royal)",
      letterSpacing:"0.06em", textTransform:"uppercase", fontWeight:700,
      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
    }}>
      {label}
    </label>
    <input
      id={id} type={type} value={value} placeholder={placeholder} step={step}
      onChange={e => onChange(e.target.value)}
      style={{
        background:"#fff", border:"2px solid var(--royal)", color:"var(--royal)",
        fontFamily:"var(--mono)", fontSize:16, padding:"11px 12px",
        borderRadius:10, outline:"none", width:"100%",
        transition:"border-color 0.2s, box-shadow 0.2s", boxSizing:"border-box",
        minWidth:0,
      }}
      onFocus={e => { e.currentTarget.style.borderColor="var(--royal-dark)"; e.currentTarget.style.boxShadow="0 0 0 3px rgba(65,105,225,0.15)"; }}
      onBlur={e  => { e.currentTarget.style.borderColor="var(--royal)";      e.currentTarget.style.boxShadow="none"; }}
    />
  </div>
);

const StatCard = ({ label, value, color = "var(--text)" }: { label:string; value:string; color?:string }) => (
  <div style={{
    background:"#f8f9fc", border:"1px solid var(--border)",
    borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:4,
  }}>
    <span style={{ fontFamily:"var(--mono)", fontSize:10, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--muted)" }}>
      {label}
    </span>
    <span style={{ fontFamily:"var(--mono)", fontSize:20, fontWeight:700, color, lineHeight:1.2 }}>
      {value}
    </span>
  </div>
);

const PrimaryButton = ({ onClick, disabled=false, children }: { onClick:()=>void; disabled?:boolean; children:ReactNode }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width:"100%", padding:15,
    background: disabled ? "var(--border)" : "linear-gradient(135deg,var(--red) 0%,var(--red-dark) 100%)",
    color: disabled ? "var(--muted)" : "#fff",
    fontFamily:"var(--sans)", fontWeight:800, fontSize:15, letterSpacing:"0.05em",
    border:"none", borderRadius:12, cursor: disabled ? "not-allowed" : "pointer",
    transition:"transform 0.15s, box-shadow 0.15s",
    display:"flex", alignItems:"center", justifyContent:"center", gap:8,
    boxShadow: disabled ? "none" : "0 4px 14px rgba(204,20,20,0.3)",
  }}
    onMouseEnter={e => { if (!disabled) { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(204,20,20,0.35)"; } }}
    onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow=disabled?"none":"0 4px 14px rgba(204,20,20,0.3)"; }}
    onMouseDown={e  => { e.currentTarget.style.transform="translateY(0)"; }}
  >{children}</button>
);

const SecondaryButton = ({ onClick, disabled=false, children }: { onClick:()=>void; disabled?:boolean; children:ReactNode }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding:"12px 24px",
    background: disabled ? "var(--border)" : "linear-gradient(135deg,var(--royal) 0%,var(--royal-dark) 100%)",
    color: disabled ? "var(--muted)" : "#fff",
    fontFamily:"var(--sans)", fontWeight:800, fontSize:14, letterSpacing:"0.04em",
    border:"none", borderRadius:12, cursor: disabled ? "not-allowed" : "pointer",
    transition:"transform 0.15s, box-shadow 0.15s",
    display:"flex", alignItems:"center", gap:8,
    boxShadow: disabled ? "none" : "0 4px 14px rgba(65,105,225,0.3)",
    whiteSpace:"nowrap", flexShrink:0,
  }}
    onMouseEnter={e => { if (!disabled) { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 20px rgba(65,105,225,0.35)"; } }}
    onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow=disabled?"none":"0 4px 14px rgba(65,105,225,0.3)"; }}
  >{children}</button>
);

const ErrorBanner = ({ message }: { message:string }) => (
  <p style={{
    marginTop:14, fontFamily:"var(--mono)", fontSize:12,
    color:"var(--red)", background:"rgba(204,20,20,0.06)",
    border:"1px solid rgba(204,20,20,0.2)", borderRadius:8, padding:"8px 12px",
  }}>⚠️ {message}</p>
);

const Disclaimer = () => (
  <p style={{ fontFamily:"var(--mono)", fontSize:10, color:"var(--muted)", textAlign:"center", opacity:0.6, padding:"0 16px" }}>
    ⚠️ Not SEBI-registered advice. For educational purposes only. Always do your own research.
  </p>
);

// ════════════════════════════════════════════════════════════
//  SECTION COMPONENTS
// ════════════════════════════════════════════════════════════

// ── PositionForm: always 2-col grid on ≥480px, single col below ──
const PositionForm = ({
  form, setField, error, onCalculate,
}: {
  form: FormState;
  setField: (key: keyof FormState) => (v: string) => void;
  error: string;
  onCalculate: () => void;
}) => (
  <Card className="fade-up">
    <CardTitle>Your Position</CardTitle>

    {/* grid: 2 cols on tablet+, 1 col on mobile via className */}
    <div className="form-grid">
      <Field label="Stock Symbol"             id="symbol"   value={form.symbol}   onChange={setField("symbol")}   placeholder="e.g. IOC" />
      <Field label="Qty Held"                 id="qty"      type="number" value={form.qty}      onChange={setField("qty")}      placeholder="52" />
      <Field label="Avg Buy Price (₹)"        id="avgPrice" type="number" value={form.avgPrice} onChange={setField("avgPrice")} placeholder="158.61" step="0.01" />
      <Field label="Current Market Price (₹)" id="ltp"      type="number" value={form.ltp}      onChange={setField("ltp")}      placeholder="144.70" step="0.01" />
      <Field label="Target Average Price (₹)" id="target"   type="number" value={form.target}   onChange={setField("target")}   placeholder="150" step="0.01" fullWidth />
    </div>

    {error && <ErrorBanner message={error} />}
    <div style={{ marginTop:20 }}>
      <PrimaryButton onClick={onCalculate}>⚡ Calculate Strategy</PrimaryButton>
    </div>
  </Card>
);

const ResultSummary = ({ result, ltp }: { result: CalculatorResult; ltp: string }) => {
  const [progressWidth, setProgressWidth] = useState(0);
  useEffect(() => {
    setProgressWidth(0);
    const t = setTimeout(() => setProgressWidth(result.progress), 50);
    return () => clearTimeout(t);
  }, [result]);

  const progressColor = result.progress >= 95 ? "var(--green)" : result.progress >= 70 ? "#f59e0b" : "#dc2626";

  return (
    <Card className="fade-up">
      <CardTitle>Result Summary</CardTitle>
      <div className="stat-grid">
        <StatCard label="Shares to Buy"        value={`${result.sharesToBuy} shares`} color="var(--red)" />
        <StatCard label="New Avg Price"         value={inr(result.newAvg)} />
        <StatCard label="Additional Investment" value={inr(result.addCost, 0)} />
        <StatCard label="Total Investment"      value={inr(result.totalCost, 0)} />
        <StatCard label="Total Qty After"       value={`${result.totalQty} shares`} />
        <StatCard
          label="Unrealised P&L (After)"
          value={`${result.pnl >= 0 ? "+" : ""}${inr(result.pnl, 0)} (${result.pnlPct.toFixed(2)}%)`}
          color={result.pnl >= 0 ? "var(--green)" : "#dc2626"}
        />
      </div>
      <div style={{ fontFamily:"var(--mono)", fontSize:12, color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:14, marginTop:4 }}>
        Breakeven Progress
      </div>
      <div style={{ background:"var(--border)", borderRadius:100, height:8, overflow:"hidden" }}>
        <div style={{ height:"100%", borderRadius:100, background:progressColor, width:`${progressWidth}%`, transition:"width 1s cubic-bezier(.4,0,.2,1)" }} />
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"var(--mono)", fontSize:10, color:"var(--muted)", marginTop:6 }}>
        <span>LTP {inr(parseFloat(ltp))}</span>
        <span>Target {inr(result.newAvg)}</span>
      </div>
    </Card>
  );
};

const ScenarioTable = ({ scenarios }: { scenarios: ScenarioRow[] }) => (
  <Card className="fade-up-1">
    <CardTitle>Buy Scenarios</CardTitle>
    <div style={{ overflowX:"auto", marginTop:4 }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"var(--mono)", fontSize:13 }}>
        <thead>
          <tr>
            {["Qty to Buy","Cost (₹)","New Avg (₹)","vs Target"].map(h => (
              <th key={h} style={{ textAlign:"left", padding:"8px 12px", fontSize:10, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--muted)", borderBottom:"2px solid var(--border)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scenarios.map(row => {
            const good = row.diff <= 0.5;
            const bClr = good ? "var(--green)" : "#dc2626";
            const bBg  = good ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)";
            const rClr = row.isTarget ? "var(--royal)" : "var(--text)";
            const cell: React.CSSProperties = {
              padding:"10px 12px", borderBottom:"1px solid #f0f1f8",
              color:rClr, fontWeight:row.isTarget ? 700 : undefined,
              background:row.isTarget ? "rgba(65,105,225,0.05)" : undefined,
            };
            return (
              <tr key={row.qty}>
                <td style={cell}>{row.qty}{row.isTarget ? " ★" : ""}</td>
                <td style={cell}>{inr(row.cost, 0)}</td>
                <td style={cell}>{inr(row.newAvg)}</td>
                <td style={{ ...cell, color:"var(--text)", fontWeight:undefined }}>
                  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:100, fontSize:10, background:bBg, color:bClr }}>
                    {row.diff >= 0 ? "+" : ""}{row.diff.toFixed(2)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Card>
);

const StrategyAnalysis = ({ result }: { result: CalculatorResult }) => {
  const verdictColor = result.verdict === "buy" ? "var(--green)" : result.verdict === "sell" ? "#dc2626" : "#d97706";
  const verdictEmoji = result.verdict === "buy" ? "🟢" : result.verdict === "sell" ? "🔴" : "🟡";
  return (
    <Card className="fade-up-2">
      <CardTitle>Strategy Analysis</CardTitle>
      <div style={{ background:"#f8f9fc", border:"1px solid var(--border)", borderRadius:12, padding:18 }}>
        <div style={{ fontFamily:"var(--mono)", fontSize:12, color:"var(--muted)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:14 }}>
          Recommendation
        </div>
        <div style={{ fontSize:18, fontWeight:800, marginBottom:10, display:"flex", alignItems:"center", gap:10, color:verdictColor }}>
          <span style={{ fontSize:22 }}>{verdictEmoji}</span>
          {result.verdictText}
        </div>
        <p style={{ fontSize:13, color:"var(--muted)", lineHeight:1.7, fontFamily:"var(--mono)" }}>
          {result.analysisText}
        </p>
      </div>
    </Card>
  );
};

const PdfExportPanel = ({ onExport, isExporting }: { onExport:()=>void; isExporting:boolean }) => (
  <div className="fade-up-3" style={{
    background:"var(--card)", border:"1.5px dashed var(--border)",
    borderRadius:16, padding:20,
    display:"flex", alignItems:"center", justifyContent:"space-between",
    gap:16, flexWrap:"wrap",
  }}>
    <div>
      <p style={{ fontFamily:"var(--sans)", fontWeight:700, fontSize:14, color:"var(--text)", marginBottom:3 }}>
        📄 Export Strategy Report
      </p>
      <p style={{ fontFamily:"var(--mono)", fontSize:11, color:"var(--muted)" }}>
        Download a clean PDF summary of this analysis
      </p>
    </div>
    <SecondaryButton onClick={onExport} disabled={isExporting}>
      {isExporting ? <><span className="spinner">⟳</span> Generating…</> : <>⬇ Download PDF</>}
    </SecondaryButton>
  </div>
);

// ════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════

const StockAvgCalculator: React.FC = () => {
  const { form, setField, result, error, exporting, calculate, exportPdf } = useCalculator();
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result) setTimeout(() => resultsRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 100);
  }, [result]);

  useEffect(() => {
    const t = setTimeout(calculate, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;800&display=swap');

        :root {
          --bg:         #ffffff;
          --surface:    #f8f9fc;
          --card:       #ffffff;
          --border:     #dde1f0;
          --red:        #cc1414;
          --red-dark:   #a50f0f;
          --royal:      #4169e1;
          --royal-dark: #2a4fc7;
          --green:      #16a34a;
          --text:       #1a1a2e;
          --muted:      #6b7280;
          --mono:       'Space Mono', monospace;
          --sans:       'Syne', sans-serif;
        }

        *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }

        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { opacity:0.4; }
        input::placeholder { color:#a5b4fc !important; opacity:1; }

        /* ── Form grid: 2-col on ≥520px, 1-col below ── */
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 519px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          /* on mobile fullWidth has no effect — everything spans 1 col naturally */
          .form-grid > * {
            grid-column: auto !important;
          }
        }

        /* ── Stat grid: 3-col → 2-col → 1-col ── */
        .stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        @media (max-width: 600px) {
          .stat-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 380px) {
          .stat-grid { grid-template-columns: 1fr; }
        }

        @keyframes fadeDown { from{opacity:0;transform:translateY(-16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeUp   { from{opacity:0;transform:translateY( 16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin     { to{transform:rotate(360deg)} }

        .fade-down { animation: fadeDown 0.6s ease both; }
        .fade-up   { animation: fadeUp   0.5s       ease both; }
        .fade-up-1 { animation: fadeUp   0.5s 0.05s ease both; }
        .fade-up-2 { animation: fadeUp   0.5s 0.10s ease both; }
        .fade-up-3 { animation: fadeUp   0.5s 0.15s ease both; }
        .spinner   { animation: spin 0.8s linear infinite; display:inline-block; }
      `}</style>

      <div style={{ background:"var(--bg)", color:"var(--text)", fontFamily:"var(--sans)", minHeight:"100vh", paddingBottom:48 }}>

        <div className="fade-down">
          <PageHeader
            title={<>Stock <Highlight>Average</Highlight></>}
            subtitle="Plan your buy-down strategy instantly"
          />
          <AccentStrip />
        </div>

        <div style={{ maxWidth:720, margin:"0 auto", padding:"28px 16px 0", display:"flex", flexDirection:"column", gap:20 }}>

          <PositionForm form={form} setField={setField} error={error} onCalculate={calculate} />

          {result && (
            <div ref={resultsRef} style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <ResultSummary  result={result} ltp={form.ltp} />
              <ScenarioTable  scenarios={result.scenarios} />
              <StrategyAnalysis result={result} />
              <PdfExportPanel onExport={exportPdf} isExporting={exporting} />
              <Disclaimer />
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default StockAvgCalculator;